import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  CheckCircle, 
  Loader2, 
  Building2, 
  Calendar, 
  Clock, 
  MapPin, 
  Languages,
  Phone,
  Mail,
  User,
  AlertTriangle,
  Send,
  Repeat
} from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PhoneInput from '../components/PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wisconsin DOA Agencies
const WISCONSIN_AGENCIES = [
  "Department of Administration (DOA)",
  "Department of Agriculture, Trade and Consumer Protection (DATCP)",
  "Department of Children and Families (DCF)",
  "Department of Corrections (DOC)",
  "Department of Employee Trust Funds (ETF)",
  "Department of Financial Institutions (DFI)",
  "Department of Health Services (DHS)",
  "Department of Justice (DOJ)",
  "Department of Military Affairs (DMA)",
  "Department of Natural Resources (DNR)",
  "Department of Public Instruction (DPI)",
  "Department of Revenue (DOR)",
  "Department of Safety and Professional Services (DSPS)",
  "Department of Tourism (Tourism)",
  "Department of Transportation (DOT)",
  "Department of Veterans Affairs (DVA)",
  "Department of Workforce Development (DWD)",
  "Office of the Commissioner of Insurance (OCI)",
  "Public Service Commission (PSC)",
  "University of Wisconsin System",
  "Wisconsin Economic Development Corporation (WEDC)",
  "Wisconsin Housing and Economic Development Authority (WHEDA)",
  "Other State Agency"
];

// Languages commonly needed
const LANGUAGES = [
  "Spanish",
  "Hmong",
  "Somali",
  "Arabic",
  "Mandarin Chinese",
  "Cantonese",
  "Vietnamese",
  "Russian",
  "Korean",
  "Burmese",
  "Nepali",
  "French",
  "German",
  "Polish",
  "Tagalog",
  "American Sign Language (ASL)",
  "Other"
];

// Interpreting types
const INTERPRETING_TYPES = [
  { value: "in_person", label: "In-Person Interpreting" },
  { value: "phone", label: "Over-the-Phone (OPI)" },
  { value: "video", label: "Video Remote (VRI)" }
];

// Service Categories for Wisconsin (based on common government needs)
const SERVICE_CATEGORIES = [
  { value: "legal", label: "Legal", subcategories: [
    { value: "court", label: "Court Proceedings" },
    { value: "deposition", label: "Depositions" },
    { value: "other_legal", label: "Other Legal" }
  ]},
  { value: "medical", label: "Medical/Healthcare", subcategories: [] },
  { value: "general", label: "General/Administrative", subcategories: [] }
];

// Frequency options
const FREQUENCY_OPTIONS = [
  { value: "one_time", label: "One-Time Appointment" },
  { value: "weekly", label: "Weekly" },
  { value: "bi_weekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom Schedule" }
];

const WisconsinRequest = () => {
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    agency: '',
    department_unit: '',
    language: '',
    other_language: '',
    service_category: '',
    service_subcategory: '',
    interpreting_type: '',
    appointment_date: '',
    appointment_time: '',
    duration_hours: '1',
    frequency: 'one_time',
    frequency_end_date: '',
    custom_frequency_notes: '',
    location_address: '',
    location_city: '',
    location_zip: '',
    appointment_type: '',
    case_number: '',
    special_instructions: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/public/wisconsin-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        setRequestId(data.work_order_id);
        setSubmitted(true);
        toast.success('Request submitted successfully!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success Screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-700 flex items-center justify-center p-6">
        <Card className="w-full max-w-lg" data-testid="wisconsin-request-success">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Request Submitted!</h1>
            <p className="text-muted-foreground mb-4">
              Your interpreting service request has been received.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-muted-foreground">Reference Number</p>
              <p className="text-xl font-bold text-blue-600" data-testid="request-reference">
                {requestId}
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-medium text-blue-800 mb-2">What's Next?</h3>
              <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                <li>Our team will review your request</li>
                <li>You'll receive a confirmation email</li>
                <li>An interpreter will be assigned based on availability</li>
                <li>You'll be notified once confirmed</li>
              </ul>
            </div>

            <Button 
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  contact_name: '',
                  contact_phone: '',
                  contact_email: '',
                  agency: '',
                  department_unit: '',
                  language: '',
                  other_language: '',
                  interpreting_type: '',
                  appointment_date: '',
                  appointment_time: '',
                  duration_hours: '1',
                  frequency: 'one_time',
                  frequency_end_date: '',
                  custom_frequency_notes: '',
                  location_address: '',
                  location_city: '',
                  location_zip: '',
                  appointment_type: '',
                  case_number: '',
                  special_instructions: ''
                });
              }}
              variant="outline"
              className="w-full"
            >
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-blue-800 to-blue-700">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
              alt="Bylyngo"
              className="h-12 w-auto"
            />
            <div className="hidden sm:block border-l pl-4">
              <p className="font-semibold text-gray-800">Wisconsin State Contract</p>
              <p className="text-sm text-gray-500">Interpreting Services Request</p>
            </div>
          </div>
          <Badge className="bg-blue-100 text-blue-800">DOA Contract</Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Notice Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Interpreting Services Only</p>
            <p className="text-sm text-amber-700">
              This Wisconsin RFP contract is for Interpreting services only. 
              Please see your contract for awarded Translation Service providers.
            </p>
          </div>
        </div>

        <Card className="shadow-xl" data-testid="wisconsin-request-form">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              Request Interpreting Services
            </CardTitle>
            <p className="text-blue-100 text-sm">
              Complete the form below to request interpreter services
            </p>
          </CardHeader>

          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 border-b pb-2">
                  <User className="h-5 w-5 text-blue-600" />
                  Contact Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Name *</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => handleChange('contact_name', e.target.value)}
                      placeholder="Full name"
                      required
                      data-testid="wi-contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Phone Number *</Label>
                    <PhoneInput
                      id="contact_phone"
                      value={formData.contact_phone}
                      onChange={(value) => handleChange('contact_phone', value)}
                      required
                      data-testid="wi-contact-phone"
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="contact_email">Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        id="contact_email"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => handleChange('contact_email', e.target.value)}
                        placeholder="your.email@wisconsin.gov"
                        className="pl-10"
                        required
                        data-testid="wi-contact-email"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Agency Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 border-b pb-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Agency Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="agency">State Agency *</Label>
                    <Select 
                      value={formData.agency} 
                      onValueChange={(v) => handleChange('agency', v)}
                      required
                    >
                      <SelectTrigger data-testid="wi-agency-select">
                        <SelectValue placeholder="Select your agency" />
                      </SelectTrigger>
                      <SelectContent>
                        {WISCONSIN_AGENCIES.map((agency) => (
                          <SelectItem key={agency} value={agency}>
                            {agency}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="department_unit">Department/Unit (Optional)</Label>
                    <Input
                      id="department_unit"
                      value={formData.department_unit}
                      onChange={(e) => handleChange('department_unit', e.target.value)}
                      placeholder="e.g., Division of Public Health"
                      data-testid="wi-department"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="case_number">Case/Reference Number (Optional)</Label>
                    <Input
                      id="case_number"
                      value={formData.case_number}
                      onChange={(e) => handleChange('case_number', e.target.value)}
                      placeholder="If applicable"
                      data-testid="wi-case-number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="appointment_type">Appointment Type (Optional)</Label>
                    <Input
                      id="appointment_type"
                      value={formData.appointment_type}
                      onChange={(e) => handleChange('appointment_type', e.target.value)}
                      placeholder="e.g., Medical, Legal, Administrative"
                      data-testid="wi-appointment-type"
                    />
                  </div>
                </div>
              </div>

              {/* Service Details */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 border-b pb-2">
                  <Languages className="h-5 w-5 text-blue-600" />
                  Service Details
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Service Category */}
                  <div className="space-y-2">
                    <Label htmlFor="service_category">Service Category *</Label>
                    <Select 
                      value={formData.service_category} 
                      onValueChange={(v) => handleChange('service_category', v)}
                      required
                    >
                      <SelectTrigger data-testid="wi-service-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Service Subcategory - only show if category has subcategories */}
                  {formData.service_category && 
                   SERVICE_CATEGORIES.find(c => c.value === formData.service_category)?.subcategories?.length > 0 && (
                    <div className="space-y-2">
                      <Label htmlFor="service_subcategory">Subcategory</Label>
                      <Select 
                        value={formData.service_subcategory} 
                        onValueChange={(v) => handleChange('service_subcategory', v)}
                      >
                        <SelectTrigger data-testid="wi-service-subcategory">
                          <SelectValue placeholder="Select subcategory" />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICE_CATEGORIES
                            .find(c => c.value === formData.service_category)?.subcategories
                            ?.map((sub) => (
                              <SelectItem key={sub.value} value={sub.value}>
                                {sub.label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="language">Language Needed *</Label>
                    <Select 
                      value={formData.language} 
                      onValueChange={(v) => handleChange('language', v)}
                      required
                    >
                      <SelectTrigger data-testid="wi-language-select">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map((lang) => (
                          <SelectItem key={lang} value={lang}>
                            {lang}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.language === 'Other' && (
                    <div className="space-y-2">
                      <Label htmlFor="other_language">Specify Language *</Label>
                      <Input
                        id="other_language"
                        value={formData.other_language}
                        onChange={(e) => handleChange('other_language', e.target.value)}
                        placeholder="Enter language"
                        required
                        data-testid="wi-other-language"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="interpreting_type">Interpreting Type *</Label>
                    <Select 
                      value={formData.interpreting_type} 
                      onValueChange={(v) => handleChange('interpreting_type', v)}
                      required
                    >
                      <SelectTrigger data-testid="wi-interpreting-type">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERPRETING_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 border-b pb-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Schedule
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="appointment_date">Date *</Label>
                    <Input
                      id="appointment_date"
                      type="date"
                      value={formData.appointment_date}
                      onChange={(e) => handleChange('appointment_date', e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      data-testid="wi-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="appointment_time">Time *</Label>
                    <Input
                      id="appointment_time"
                      type="time"
                      value={formData.appointment_time}
                      onChange={(e) => handleChange('appointment_time', e.target.value)}
                      required
                      data-testid="wi-time"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duration_hours">Estimated Duration *</Label>
                    <Select 
                      value={formData.duration_hours} 
                      onValueChange={(v) => handleChange('duration_hours', v)}
                    >
                      <SelectTrigger data-testid="wi-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0.5">30 minutes</SelectItem>
                        <SelectItem value="1">1 hour</SelectItem>
                        <SelectItem value="1.5">1.5 hours</SelectItem>
                        <SelectItem value="2">2 hours</SelectItem>
                        <SelectItem value="3">3 hours</SelectItem>
                        <SelectItem value="4">4 hours</SelectItem>
                        <SelectItem value="8">Full day (8 hours)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Frequency */}
                <div className="bg-blue-50 rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-2">
                    <Repeat className="h-5 w-5 text-blue-600" />
                    <Label className="font-medium">Recurring Appointment?</Label>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="frequency">Frequency</Label>
                      <Select 
                        value={formData.frequency} 
                        onValueChange={(v) => handleChange('frequency', v)}
                      >
                        <SelectTrigger data-testid="wi-frequency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FREQUENCY_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {formData.frequency !== 'one_time' && (
                      <div className="space-y-2">
                        <Label htmlFor="frequency_end_date">End Date (Optional)</Label>
                        <Input
                          id="frequency_end_date"
                          type="date"
                          value={formData.frequency_end_date}
                          onChange={(e) => handleChange('frequency_end_date', e.target.value)}
                          min={formData.appointment_date}
                          data-testid="wi-frequency-end"
                        />
                      </div>
                    )}
                  </div>

                  {formData.frequency === 'custom' && (
                    <div className="space-y-2">
                      <Label htmlFor="custom_frequency_notes">Custom Schedule Details</Label>
                      <Textarea
                        id="custom_frequency_notes"
                        value={formData.custom_frequency_notes}
                        onChange={(e) => handleChange('custom_frequency_notes', e.target.value)}
                        placeholder="e.g., Every Monday and Wednesday at 9 AM"
                        rows={2}
                        data-testid="wi-custom-frequency"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Location (for in-person) */}
              {formData.interpreting_type === 'in_person' && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 border-b pb-2">
                    <MapPin className="h-5 w-5 text-blue-600" />
                    Location
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2 md:col-span-3">
                      <Label htmlFor="location_address">Street Address *</Label>
                      <AddressAutocomplete
                        id="location_address"
                        value={formData.location_address}
                        onChange={(value) => handleChange('location_address', value)}
                        onSelect={(place) => {
                          // Auto-fill city and zip when address is selected
                          if (place.city) handleChange('location_city', place.city);
                          if (place.zip) handleChange('location_zip', place.zip);
                          // Update full street address
                          const streetAddr = [place.street_number, place.street].filter(Boolean).join(' ');
                          if (streetAddr) handleChange('location_address', streetAddr);
                        }}
                        placeholder="Start typing address..."
                        data-testid="wi-address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location_city">City *</Label>
                      <Input
                        id="location_city"
                        value={formData.location_city}
                        onChange={(e) => handleChange('location_city', e.target.value)}
                        placeholder="Madison"
                        required={formData.interpreting_type === 'in_person'}
                        data-testid="wi-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>State</Label>
                      <Input value="Wisconsin" disabled className="bg-gray-100" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="location_zip">ZIP Code *</Label>
                      <Input
                        id="location_zip"
                        value={formData.location_zip}
                        onChange={(e) => handleChange('location_zip', e.target.value)}
                        placeholder="53703"
                        required={formData.interpreting_type === 'in_person'}
                        data-testid="wi-zip"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Special Instructions */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-gray-800 border-b pb-2">
                  Additional Information
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="special_instructions">Special Instructions or Notes (Optional)</Label>
                  <Textarea
                    id="special_instructions"
                    value={formData.special_instructions}
                    onChange={(e) => handleChange('special_instructions', e.target.value)}
                    placeholder="Any special requirements, parking instructions, contact details for the day of appointment, etc."
                    rows={3}
                    data-testid="wi-instructions"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
                  disabled={submitting || !formData.contact_name || !formData.contact_email || !formData.agency || !formData.language || !formData.interpreting_type || !formData.appointment_date || !formData.appointment_time}
                  data-testid="wi-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Submitting Request...
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  By submitting, you confirm this request is authorized under the Wisconsin DOA interpreting services contract.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-blue-200 text-sm">
          <p>Questions? Contact Bylyngo at (414) 326-9565 or info@bylyngo.com</p>
          <p className="mt-2">© {new Date().getFullYear()} Bylyngo Interpreting & Translation</p>
        </div>
      </main>
    </div>
  );
};

export default WisconsinRequest;
